<script>
import {useVuelidate} from '@vuelidate/core';
import {helpers, maxLength, required} from '@vuelidate/validators';

/* COMPOSANTS */
import AkInputText from '@components/v2/input/AkInputText.vue';
import AkDropdown from '@components/v2/input/AkDropdown.vue';
import GaMessages from '@components/ga/layout/GaMessages.vue';
import AkCalendar from '@components/v2/input/AkCalendar.vue';
import AkFilesUpload from '@components/v2/input/AkFilesUpload.vue';
import AkSidebar from '@components/v2/general/AkSidebar.vue';
import AkFicItem from '@components/v2/layout/AkFicItem.vue';
import AkButtonFooterSidebar from '@components/v2/general/AkButtonFooterSidebar.vue';
import AkInputPhone from '@components/v2/input/AkInputPhone.vue';
import AkFormSubmitted from '@components/general/AkFormSubmitted.vue';

/* MIXINS */
import messageMixin from '@mixins/messageMixin';
import civilitiesConst from '@mixins/const/civilitiesConst';
import companyMixin from '@mixins/companyMixin';
import permissionsMixin from '@mixins/permissionsMixin';
import formatMixin from '@mixins/formatMixin';
import validatorMixins from '@mixins/validatorMixins';

/* SERVICES */
import prestataireService from '@services/prestataireService';
import ficService from '@services/ficService';

export default {
  components: {
    AkButtonFooterSidebar,
    AkFicItem,
    AkSidebar,
    AkFilesUpload,
    AkCalendar,
    GaMessages,
    AkDropdown,
    AkInputText,
    AkInputPhone,
    AkFormSubmitted,
  },
  mixins: [messageMixin, companyMixin, permissionsMixin, formatMixin, validatorMixins, civilitiesConst],
  setup() {
    return {v$: useVuelidate()};
  },
  data() {
    return {
      files: [],
      updateSidebarVisible: false,
      prestataire: {},
      submitted: false,
      loading: true,
    };
  },
  validations() {
    return {
      prestataire: {
        contact: {
          civility: {required},
          firstName: {
            required,
            maxLengthValue: maxLength(100),
            customPattern: helpers.withMessage(this.$t('bad_text_format'), this.yousignValidator),
          },
          lastName: {
            required,
            maxLengthValue: maxLength(100),
            customPattern: helpers.withMessage(this.$t('bad_text_format'), this.yousignValidator),
          },
          phone: {required, phoneValidator: this.phoneValidator},
          homePhone: {phoneValidator: this.phoneValidator},
        },
        companyId: {required},
      },
    };
  },
  emits: ['update', 'updateFile', 'hide'],
  methods: {
    show(id) {
      this.updateSidebarVisible = true;
      this.submitted = false;
      this.prestataireId = id;
      this.reload();
    },
    hide() {
      this.updateSidebarVisible = false;
      this.prestataire = undefined;
      this.prestataireId = undefined;
      this.files = [];
      this.$emit('hide');
    },
    reload() {
      prestataireService.get(this.prestataireId).then((data) => {
        this.prestataire = data;
      });
    },
    update() {
      if (this.prestataireId === -1) return;

      this.submitted = true;
      this.v$.prestataire.$touch();
      if (this.v$.prestataire.$error) {
        this.$refs.footerSidebar.init();
        return;
      }

      prestataireService
        .update(this.prestataireId, this.prestataire, this.files)
        .then(() => {
          this.success(this.$t('prestataire.updated'));
          this.prestataire = {};
          this.$emit('update');
          this.hide();
        })
        .catch((e) => {
          const msg = this.msgForErr(this.$t, e);
          this.$refs.sidebarMessages.error(msg);
          this.$refs.footerSidebar.init();
        })
        .finally(() => {
          this.submitted = false;
        });
    },
    deleteFile(file) {
      ficService
        .delete(file)
        .then(() => {
          this.prestataire.files = this.prestataire.files.filter((f) => f.id !== file.id);
          this.$emit('updateFile');
          this.success('Le fichier a bien été supprimé');
        })
        .catch((e) => {
          this.addError('Une erreur est survenue');
        });
    },
  },
};
</script>

<template>
  <AkSidebar
    v-model:visible="updateSidebarVisible"
    :baseZIndex="9998"
    :dismissable="true"
    block-scroll
    position="right"
    @hide="hide">
    <template #container>
      <div class="mb-4">
        <GaMessages ref="sidebarMessages" />
      </div>
      <div class="p-sidebar-header">
        <div class="p-sidebar-header-content">{{ $t('prestataire.update') }}</div>
        <button class="p-sidebar-close p-sidebar-icon p-link" type="button" @click="hide">
          <span
            class="p-sidebar-close-icon ga-icon ga-close_circle text-4xl text-grey-400 md:hover:text-grey-900 md:hover:bg-transparent" />
        </button>
      </div>
      <div v-if="prestataire?.contact" class="p-sidebar-content">
        <perfect-scrollbar class="h-full pr-7" :options="{suppressScrollX: true}">
          <AkFormSubmitted :submitted="submitted" class="flex flex-col gap-4 md:gap-2" reference="form">
            <span class="title-border secondary text-grey-900 text-xl font-bold mb-3">Informations</span>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-2">
              <AkDropdown
                v-model="prestataire.contact.civility"
                :label="$t('prestataire.civility')"
                :options="civilitiesConst"
                :validator="v$.prestataire.contact.civility" />
              <AkCalendar
                v-model="prestataire.contact.birthDate"
                :date-format="'dd/mm/yy'"
                :label="$t('prestataire.birthDate')"
                :maxDate="new Date()"
                :validator="v$.prestataire.contact.birthDate" />
              <AkInputText
                v-model="prestataire.contact.lastName"
                :formatter="toUppercase"
                :label="$t('prestataire.lastName')"
                :validator="v$.prestataire.contact.lastName" />
              <AkInputText
                v-model="prestataire.contact.firstName"
                :label="$t('prestataire.firstName')"
                :validator="v$.prestataire.contact.firstName" />
            </div>
            <span class="title-border secondary text-grey-900 text-xl font-bold mb-3">Coordonnées</span>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-2">
              <AkInputPhone
                v-model="prestataire.contact.phone"
                :label="$t('prestataire.phone')"
                :validator="v$.prestataire.contact.phone"
                type="tel" />
              <AkInputPhone
                v-model="prestataire.contact.homePhone"
                :label="$t('prestataire.homePhone')"
                :validator="v$.prestataire.contact.homePhone"
                type="tel" />
            </div>
            <template v-if="isGas()">
              <span class="title-border secondary text-grey-900 text-xl font-bold mb-3"
                >Informations complémentaires</span
              >
              <AkDropdown
                v-model="prestataire.companyId"
                :label="$t('prestataire.company')"
                :options="companies"
                :validator="v$.prestataire.companyId"
                option-label="name"
                option-value="id" />
            </template>
            <span class="title-border secondary text-grey-900 text-xl font-bold mb-3">{{ $t('files') }}</span>
            <div v-if="prestataire.files && prestataire.files.length > 0" class="grid grid-cols-1 mt-4 gap-4 md:gap-2">
              <div v-for="(file, idx) of prestataire.files" :key="file" class="flex flex-col gap-2">
                <AkFicItem v-model="prestataire.files[idx]" @delete="deleteFile(file)" />
              </div>
            </div>
            <AkFilesUpload v-model="files" multiple />

            <AkButtonFooterSidebar
              ref="footerSidebar"
              :cancel-label="$t('cancel')"
              :validate-label="$t('update')"
              @action="update()"
              @cancel="hide()" />
          </AkFormSubmitted>
        </perfect-scrollbar>
      </div>
    </template>
  </AkSidebar>
</template>
